<template>
  <div class="dis-page">
    <b-row>
      <!-- Panel lateral -->
      <b-col lg="4" xl="3">
        <!-- Impresora -->
        <b-card no-body class="mb-2">
          <b-card-body>
            <label class="font-weight-bold d-flex align-items-center mb-75">
              <i class="fa-solid fa-print mr-50 text-primary"></i> Impresora
            </label>
            <b-form-select v-model="printerId" :options="printerOptions" @change="guardarImpresora">
              <template #first>
                <b-form-select-option :value="null" disabled>
                  -- Selecciona impresora --
                </b-form-select-option>
              </template>
            </b-form-select>
            <div class="text-right mt-50">
              <b-button size="sm" variant="flat-secondary" class="px-50" @click="cargarImpresoras">
                <i class="fa-solid fa-rotate"></i> Recargar
              </b-button>
            </div>
            <b-alert show variant="warning" class="mb-0 mt-50 p-75">
              <small>
                <i class="fa-solid fa-circle-info mr-25"></i>
                El ordenador de <b>Stockroom</b> tiene que estar encendido para poder imprimir.
              </small>
            </b-alert>
          </b-card-body>
        </b-card>

        <!-- Medida -->
        <b-card no-body class="mb-2">
          <b-card-body>
            <label class="font-weight-bold d-flex align-items-center mb-75">
              <i class="fa-solid fa-ruler-combined mr-50 text-primary"></i> Medida de etiqueta
            </label>
            <b-form-select v-model="medidaId" :options="medidaOptions" @change="onMedidaChange">
              <template #first>
                <b-form-select-option :value="null" disabled>
                  -- Selecciona medida --
                </b-form-select-option>
              </template>
            </b-form-select>
            <div class="d-flex justify-content-between mt-50">
              <div>
                <b-button size="sm" variant="flat-danger" class="px-50" :disabled="!medidaId" @click="borrarMedida">
                  <i class="fa-solid fa-trash"></i>
                </b-button>
                <b-button size="sm" variant="flat-secondary" class="px-50" :disabled="!medidaId" title="Editar medida" @click="abrirEditarMedida">
                  <i class="fa-solid fa-pen"></i>
                </b-button>
              </div>
              <b-button size="sm" variant="flat-primary" @click="abrirNuevaMedida">
                <i class="fa-solid fa-plus mr-25"></i> Nueva medida
              </b-button>
            </div>
          </b-card-body>
        </b-card>

        <!-- Imprimir -->
        <b-card no-body class="mb-2">
          <b-card-body>
            <label class="font-weight-bold d-flex align-items-center mb-75">
              <i class="fa-solid fa-print mr-50 text-primary"></i> Imprimir
            </label>
            <b-row class="align-items-end">
              <b-col cols="5">
                <label class="small mb-25">Copias</label>
                <b-form-input v-model.number="copias" type="number" min="1" max="500" size="sm" />
              </b-col>
              <b-col cols="7">
                <b-button variant="outline-secondary" size="sm" block :disabled="!puedeImprimir" @click="verPreview">
                  <i class="fa-solid fa-eye mr-25"></i> Preview
                </b-button>
              </b-col>
            </b-row>
            <b-button variant="primary" block class="mt-1" :disabled="!puedeImprimir || imprimiendo" @click="imprimir">
              <b-spinner v-if="imprimiendo" small class="mr-50" />
              <i v-else class="fa-solid fa-print mr-50"></i>
              Imprimir
            </b-button>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Lienzo + opciones de elementos al lado -->
      <b-col lg="8" xl="9">
        <b-row>
          <b-col cols="12" md="7" xl="8">
        <b-card no-body>
          <b-card-body class="dis-lienzo-wrap">
            <div v-if="!medida" class="text-center text-muted py-5">
              <i class="fa-solid fa-ruler-combined fa-2x mb-1 d-block"></i>
              Selecciona o crea una medida de etiqueta para empezar
            </div>
            <template v-else>
              <div class="text-muted small mb-75 text-center">
                {{ medida.nombre }} — {{ medida.anchoMm }} x {{ medida.altoMm }} mm
                (arrastra los elementos para colocarlos)
              </div>
              <div
                ref="lienzo"
                class="dis-lienzo"
                :style="{ width: medida.anchoMm * escala + 'px', height: medida.altoMm * escala + 'px' }"
                @mousedown.self="seleccionadoId = null"
              >
                <div
                  v-for="el in elementos"
                  :key="el.id"
                  class="dis-el"
                  :class="{ 'dis-el--sel': el.id === seleccionadoId }"
                  :style="estiloElemento(el)"
                  @mousedown.prevent="empezarDrag(el, $event)"
                >
                  <span
                    v-if="el.tipo === 'texto'"
                    class="dis-el-texto"
                    :style="{
                      fontSize: el.fontMm * escala + 'px',
                      fontWeight: el.negrita ? 'bold' : 'normal',
                    }"
                  >{{ el.texto || 'Texto' }}</span>
                  <img
                    v-else
                    :src="el.dataUrl"
                    :style="{ width: el.anchoMm * escala + 'px' }"
                    draggable="false"
                  >
                </div>
              </div>
            </template>
          </b-card-body>
        </b-card>
          </b-col>

          <b-col cols="12" md="5" xl="4">
        <!-- Añadir elementos -->
        <b-card no-body class="mb-2">
          <b-card-body>
            <label class="font-weight-bold d-flex align-items-center mb-75">
              <i class="fa-solid fa-shapes mr-50 text-primary"></i> Elementos
            </label>
            <div class="d-flex">
              <b-button variant="outline-primary" size="sm" class="mr-50" :disabled="!medida" @click="agregarTexto">
                <i class="fa-solid fa-font mr-25"></i> Texto
              </b-button>
              <b-button variant="outline-primary" size="sm" :disabled="!medida" @click="$refs.pngInput.click()">
                <i class="fa-solid fa-image mr-25"></i> PNG
              </b-button>
              <input ref="pngInput" type="file" accept="image/png" class="d-none" @change="agregarPng">
              <b-button variant="outline-danger" size="sm" class="ml-auto" :disabled="!elementos.length" @click="limpiarElementos">
                <i class="fa-solid fa-broom mr-25"></i> Limpiar
              </b-button>
            </div>

            <!-- Lista de elementos: click para seleccionarlos (útil si uno tapa a otro) -->
            <div v-if="elementos.length" class="dis-lista-el mt-1">
              <div
                v-for="el in elementos"
                :key="el.id"
                class="dis-lista-item"
                :class="{ 'dis-lista-item--sel': el.id === seleccionadoId }"
                @click="seleccionadoId = el.id"
              >
                <i v-if="el.tipo === 'texto'" class="fa-solid fa-font dis-lista-icono"></i>
                <img v-else :src="el.dataUrl" class="dis-lista-thumb">
                <span class="dis-lista-nombre">
                  {{ el.tipo === 'texto' ? (el.texto || 'Texto') : 'Imagen PNG' }}
                </span>
                <b-button
                  size="sm"
                  variant="flat-danger"
                  class="btn-icon dis-lista-borrar"
                  title="Eliminar"
                  @click.stop="eliminarElemento(el)"
                >
                  <i class="fa-solid fa-trash"></i>
                </b-button>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <!-- Propiedades del elemento seleccionado -->
        <b-card v-if="elementoSel" no-body class="mb-2">
          <b-card-body>
            <label class="font-weight-bold d-flex align-items-center mb-75">
              <i class="fa-solid fa-sliders mr-50 text-primary"></i>
              {{ elementoSel.tipo === 'texto' ? 'Texto' : 'Imagen' }}
            </label>

            <template v-if="elementoSel.tipo === 'texto'">
              <b-form-textarea v-model="elementoSel.texto" rows="2" size="sm" class="mb-75" placeholder="Escribe el texto..." />
              <div class="d-flex align-items-center justify-content-between mb-25">
                <label class="small mb-0">Tamaño de letra (mm)</label>
                <b-form-input
                  v-model.number="elementoSel.fontMm"
                  type="number"
                  min="1"
                  max="30"
                  step="0.5"
                  size="sm"
                  class="dis-input-mm"
                />
              </div>
              <b-form-input v-model.number="elementoSel.fontMm" type="range" min="1" max="30" step="0.5" />
              <b-form-checkbox v-model="elementoSel.negrita" class="mt-25" switch>
                Negrita
              </b-form-checkbox>
            </template>

            <template v-else>
              <div class="d-flex align-items-center justify-content-between mb-25">
                <label class="small mb-0">Ancho (mm)</label>
                <b-form-input
                  v-model.number="elementoSel.anchoMm"
                  type="number"
                  min="1"
                  :max="medida ? medida.anchoMm : 100"
                  step="0.5"
                  size="sm"
                  class="dis-input-mm"
                />
              </div>
              <b-form-input v-model.number="elementoSel.anchoMm" type="range" min="1" :max="medida ? medida.anchoMm : 100" step="0.5" />
            </template>

            <label class="small mb-25 d-block mt-1 text-center">Alinear</label>
            <div class="dis-alinear-wrap">
              <div class="dis-grid-alinear">
                <b-button
                  v-for="pos in posicionesAlinear"
                  :key="pos.title"
                  size="sm"
                  variant="outline-secondary"
                  :title="pos.title"
                  @click="alinearPos(elementoSel, pos.x, pos.y)"
                >
                  <i :class="pos.icon" :style="{ transform: 'rotate(' + pos.rot + 'deg)' }"></i>
                </b-button>
              </div>
              <div class="dis-alinear-ejes">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  title="Centrar horizontal (mantiene la altura)"
                  @click="alinear(elementoSel, 'x', 'centro')"
                >
                  <i class="fa-solid fa-arrows-left-right"></i>
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  title="Centrar vertical (mantiene la horizontal)"
                  @click="alinear(elementoSel, 'y', 'centro')"
                >
                  <i class="fa-solid fa-arrows-up-down"></i>
                </b-button>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-1">
              <small class="text-muted">
                x: {{ Math.round(elementoSel.x * 10) / 10 }} · y: {{ Math.round(elementoSel.y * 10) / 10 }} mm
              </small>
              <b-button size="sm" variant="flat-danger" @click="eliminarElemento(elementoSel)">
                <i class="fa-solid fa-trash mr-25"></i> Eliminar
              </b-button>
            </div>
          </b-card-body>
        </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- Modal crear/editar medida -->
    <b-modal
      v-model="showNuevaMedida"
      :title="nuevaMedida.id ? 'Editar medida de etiqueta' : 'Nueva medida de etiqueta'"
      ok-title="Guardar"
      cancel-title="Cancelar"
      :ok-disabled="!nuevaMedida.nombre || !nuevaMedida.anchoMm || !nuevaMedida.altoMm"
      @ok="guardarMedida"
    >
      <label class="small mb-25">Nombre</label>
      <b-form-input v-model="nuevaMedida.nombre" placeholder="Ej: Caja grande 100x50" class="mb-75" />
      <b-row>
        <b-col cols="6">
          <label class="small mb-25">Ancho (mm)</label>
          <b-form-input v-model.number="nuevaMedida.anchoMm" type="number" min="1" max="500" />
        </b-col>
        <b-col cols="6">
          <label class="small mb-25">Alto (mm)</label>
          <b-form-input v-model.number="nuevaMedida.altoMm" type="number" min="1" max="500" />
        </b-col>
      </b-row>
    </b-modal>

    <!-- Modal preview de impresión (bitmap real que se envía a la impresora) -->
    <b-modal v-model="showPreview" title="Vista previa de impresión" ok-only ok-title="Cerrar" size="lg">
      <div class="text-center">
        <img v-if="previewUrl" :src="previewUrl" class="dis-preview-img">
        <div class="text-muted small mt-50">
          Así saldrá de la impresora (blanco y negro, {{ DPMM }} dots/mm)
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'

// Zebra a 203 dpi = 8 dots/mm (igual que el resto de etiquetas del ERP)
const DPMM = 8
// Umbral de luminancia para pasar a blanco/negro (0-255): más alto = más tinta
const UMBRAL = 190

export default {
  data() {
    return {
      DPMM,
      printers: [],
      printerId: null,
      medidas: [],
      medidaId: null,
      elementos: [],
      seleccionadoId: null,
      siguienteId: 1,
      copias: 1,
      imprimiendo: false,
      showNuevaMedida: false,
      // id null = crear; con id = editar la existente
      nuevaMedida: { id: null, nombre: '', anchoMm: null, altoMm: null },
      showPreview: false,
      previewUrl: '',
      drag: null,
      // Cuadrícula 3x3 de alineación: cada celda fija ambos ejes de golpe.
      // Las diagonales se dibujan rotando la flecha (FA free no trae diagonales).
      posicionesAlinear: [
        { x: 'inicio', y: 'inicio', icon: 'fa-solid fa-arrow-up', rot: -45, title: 'Arriba izquierda' },
        { x: 'centro', y: 'inicio', icon: 'fa-solid fa-arrow-up', rot: 0, title: 'Arriba centro' },
        { x: 'fin', y: 'inicio', icon: 'fa-solid fa-arrow-up', rot: 45, title: 'Arriba derecha' },
        { x: 'inicio', y: 'centro', icon: 'fa-solid fa-arrow-left', rot: 0, title: 'Izquierda centro' },
        { x: 'centro', y: 'centro', icon: 'fa-solid fa-arrows-to-dot', rot: 0, title: 'Centro' },
        { x: 'fin', y: 'centro', icon: 'fa-solid fa-arrow-right', rot: 0, title: 'Derecha centro' },
        { x: 'inicio', y: 'fin', icon: 'fa-solid fa-arrow-down', rot: 45, title: 'Abajo izquierda' },
        { x: 'centro', y: 'fin', icon: 'fa-solid fa-arrow-down', rot: 0, title: 'Abajo centro' },
        { x: 'fin', y: 'fin', icon: 'fa-solid fa-arrow-down', rot: -45, title: 'Abajo derecha' },
      ],
    }
  },
  computed: {
    printerOptions() {
      return this.printers.map(p => ({
        value: p.id,
        text: `${p.name}${p.computer ? ` (${p.computer})` : ''}`,
      }))
    },
    medidaOptions() {
      return this.medidas.map(m => ({
        value: m.id,
        text: `${m.nombre} — ${Number(m.anchoMm)} x ${Number(m.altoMm)} mm`,
      }))
    },
    medida() {
      const m = this.medidas.find(x => x.id === this.medidaId)
      if (!m) return null
      return { ...m, anchoMm: Number(m.anchoMm), altoMm: Number(m.altoMm) }
    },
    // Píxeles por mm en pantalla: la etiqueta ocupa como mucho ~480px de ancho
    escala() {
      if (!this.medida) return 4
      return Math.min(8, Math.round((480 / this.medida.anchoMm) * 10) / 10)
    },
    elementoSel() {
      return this.elementos.find(e => e.id === this.seleccionadoId) || null
    },
    puedeImprimir() {
      return this.medida && this.elementos.length > 0
    },
  },
  created() {
    axios.post('/uso', { seccion: 'etiquetas-disenador' }).catch(() => {})
    this.cargarImpresoras()
    this.cargarMedidas()
  },
  mounted() {
    document.addEventListener('mousemove', this.moverDrag)
    document.addEventListener('mouseup', this.soltarDrag)
    document.addEventListener('keydown', this.moverConFlechas)
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.moverDrag)
    document.removeEventListener('mouseup', this.soltarDrag)
    document.removeEventListener('keydown', this.moverConFlechas)
  },
  methods: {
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, {
        title: variant === 'danger' ? 'Error' : 'Etiquetas',
        variant,
        solidColor: true,
      })
    },
    // ----- Impresora (misma config compartida que la sección Labels) -----
    cargarImpresoras() {
      axios.get('/etiquetas/config')
        .then(({ data }) => { this.printerId = data.printerId || null })
        .catch(() => {})
        .finally(() => {
          axios.get('/etiquetas/printers')
            .then(({ data }) => {
              this.printers = data || []
              const existeGuardada = this.printerId && this.printers.some(p => p.id === this.printerId)
              const porDefecto = this.printers.find(p => p.default)
              if (!existeGuardada) {
                this.printerId = porDefecto ? porDefecto.id : (this.printers[0] && this.printers[0].id) || null
              }
            })
            .catch(e => {
              const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudieron cargar las impresoras'
              this.notificar(msg, 'danger')
            })
        })
    },
    guardarImpresora() {
      axios.put('/etiquetas/config', { printerId: this.printerId }).catch(() => {
        this.notificar('No se pudo guardar la impresora', 'danger')
      })
    },
    // ----- Medidas -----
    cargarMedidas() {
      axios.get('/etiquetas/medidas')
        .then(({ data }) => {
          this.medidas = data || []
          if (!this.medidaId && this.medidas.length) this.medidaId = this.medidas[0].id
        })
        .catch(() => { this.notificar('No se pudieron cargar las medidas', 'danger') })
    },
    abrirNuevaMedida() {
      this.nuevaMedida = { id: null, nombre: '', anchoMm: null, altoMm: null }
      this.showNuevaMedida = true
    },
    abrirEditarMedida() {
      if (!this.medida) return
      this.nuevaMedida = {
        id: this.medida.id,
        nombre: this.medida.nombre,
        anchoMm: this.medida.anchoMm,
        altoMm: this.medida.altoMm,
      }
      this.showNuevaMedida = true
    },
    guardarMedida() {
      const editando = !!this.nuevaMedida.id
      const peticion = editando
        ? axios.put(`/etiquetas/medidas/${this.nuevaMedida.id}`, this.nuevaMedida)
        : axios.post('/etiquetas/medidas', this.nuevaMedida)
      peticion
        .then(({ data }) => {
          this.notificar(editando ? 'Medida actualizada' : 'Medida creada')
          axios.get('/etiquetas/medidas').then(({ data: rows }) => {
            this.medidas = rows || []
            if (!editando) {
              this.medidaId = data.id
              this.onMedidaChange()
            }
            // Al editar se mantienen los elementos del lienzo: solo cambia el tamaño
          })
        })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo guardar la medida'
          this.notificar(msg, 'danger')
        })
    },
    borrarMedida() {
      const m = this.medida
      if (!m) return
      this.$bvModal.msgBoxConfirm(`Se eliminará "${m.nombre}" (${m.anchoMm} x ${m.altoMm} mm).`, {
        title: 'Eliminar medida',
        okTitle: 'Eliminar',
        okVariant: 'danger',
        cancelTitle: 'Cancelar',
        cancelVariant: 'outline-secondary',
        centered: true,
      }).then(confirmado => {
        if (!confirmado) return
        axios.delete(`/etiquetas/medidas/${this.medidaId}`)
          .then(() => {
            this.medidaId = null
            this.onMedidaChange()
            this.cargarMedidas()
          })
          .catch(() => { this.notificar('No se pudo eliminar la medida', 'danger') })
      })
    },
    onMedidaChange() {
      // Al cambiar de medida se limpia el lienzo (las posiciones no tendrían sentido)
      this.elementos = []
      this.seleccionadoId = null
    },
    // ----- Elementos -----
    // Tamaño de un elemento en mm (el texto se mide con el mismo font que se imprime)
    medirElemento(el) {
      if (el.tipo === 'png') {
        return { w: el.anchoMm, h: el.anchoMm * el.ratio }
      }
      if (!this.ctxMedidas) {
        this.ctxMedidas = document.createElement('canvas').getContext('2d')
      }
      const px = el.fontMm * DPMM
      this.ctxMedidas.font = `${el.negrita ? 'bold ' : ''}${px}px Arial, sans-serif`
      const lineas = String(el.texto || '').split('\n')
      const w = Math.max(...lineas.map(l => this.ctxMedidas.measureText(l).width)) / DPMM
      return { w, h: lineas.length * el.fontMm * 1.2 }
    },
    // Alinea un eje: 'x' (izquierda/centro/derecha) o 'y' (arriba/centro/abajo)
    alinear(el, eje, modo) {
      const { w, h } = this.medirElemento(el)
      const total = eje === 'x' ? this.medida.anchoMm : this.medida.altoMm
      const tam = eje === 'x' ? w : h
      let pos = 0
      if (modo === 'centro') pos = (total - tam) / 2
      if (modo === 'fin') pos = total - tam
      el[eje] = Math.max(0, Math.round(pos * 10) / 10)
    },
    // Celda de la cuadrícula 3x3: fija los dos ejes a la vez
    alinearPos(el, modoX, modoY) {
      this.alinear(el, 'x', modoX)
      this.alinear(el, 'y', modoY)
    },
    centrarElemento(el) {
      this.alinearPos(el, 'centro', 'centro')
    },
    agregarTexto() {
      const el = {
        id: this.siguienteId++,
        tipo: 'texto',
        texto: 'Texto',
        fontMm: 8,
        negrita: false,
        x: 2,
        y: 2,
      }
      this.centrarElemento(el)
      this.elementos.push(el)
      this.seleccionadoId = el.id
    },
    agregarPng(ev) {
      const file = ev.target.files && ev.target.files[0]
      ev.target.value = ''
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        const img = new Image()
        img.onload = () => {
          const anchoMm = Math.min(this.medida.anchoMm / 2, 30)
          const el = {
            id: this.siguienteId++,
            tipo: 'png',
            dataUrl: reader.result,
            img,
            ratio: img.naturalHeight / img.naturalWidth,
            anchoMm,
            x: 2,
            y: 2,
          }
          this.centrarElemento(el)
          this.elementos.push(el)
          this.seleccionadoId = el.id
        }
        img.src = reader.result
      }
      reader.readAsDataURL(file)
    },
    eliminarElemento(el) {
      this.elementos = this.elementos.filter(e => e.id !== el.id)
      if (this.seleccionadoId === el.id) this.seleccionadoId = null
    },
    limpiarElementos() {
      this.$bvModal.msgBoxConfirm('Se quitarán todos los elementos del diseño.', {
        title: 'Limpiar elementos',
        okTitle: 'Limpiar',
        okVariant: 'danger',
        cancelTitle: 'Cancelar',
        cancelVariant: 'outline-secondary',
        centered: true,
      }).then(confirmado => {
        if (!confirmado) return
        this.elementos = []
        this.seleccionadoId = null
      })
    },
    estiloElemento(el) {
      return {
        left: el.x * this.escala + 'px',
        top: el.y * this.escala + 'px',
        // El seleccionado se pone delante para poder arrastrarlo aunque
        // otro elemento (p. ej. un PNG grande) lo tape
        zIndex: el.id === this.seleccionadoId ? 2 : 1,
      }
    },
    // ----- Drag -----
    empezarDrag(el, ev) {
      this.seleccionadoId = el.id
      this.drag = {
        el,
        startX: ev.clientX,
        startY: ev.clientY,
        origX: el.x,
        origY: el.y,
      }
    },
    moverDrag(ev) {
      if (!this.drag || !this.medida) return
      const dxMm = (ev.clientX - this.drag.startX) / this.escala
      const dyMm = (ev.clientY - this.drag.startY) / this.escala
      // Se permite sobresalir por cualquier lado; el clamp solo evita perder el
      // elemento fuera del lienzo (siempre queda al menos 1mm dentro)
      const { w, h } = this.medirElemento(this.drag.el)
      this.drag.el.x = Math.min(Math.max(this.drag.origX + dxMm, 1 - w), this.medida.anchoMm - 1)
      this.drag.el.y = Math.min(Math.max(this.drag.origY + dyMm, 1 - h), this.medida.altoMm - 1)
    },
    soltarDrag() {
      this.drag = null
    },
    // Mover el elemento seleccionado con las flechas del teclado
    // (0.5 mm por pulsación; con Shift, ajuste fino de 0.1 mm)
    moverConFlechas(ev) {
      const el = this.elementoSel
      if (!el || !this.medida) return
      // No interferir cuando se está escribiendo en un input/textarea
      const tag = (ev.target && ev.target.tagName) || ''
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      const paso = ev.shiftKey ? 0.1 : 0.5
      let dx = 0
      let dy = 0
      if (ev.key === 'ArrowLeft') dx = -paso
      else if (ev.key === 'ArrowRight') dx = paso
      else if (ev.key === 'ArrowUp') dy = -paso
      else if (ev.key === 'ArrowDown') dy = paso
      else return

      ev.preventDefault() // que no haga scroll la página
      const { w, h } = this.medirElemento(el)
      el.x = Math.round(Math.min(Math.max(el.x + dx, 1 - w), this.medida.anchoMm - 1) * 10) / 10
      el.y = Math.round(Math.min(Math.max(el.y + dy, 1 - h), this.medida.altoMm - 1) * 10) / 10
    },
    // ----- Rasterizado e impresión -----
    // Dibuja la etiqueta a resolución de impresora (dpmm dots/mm) en un canvas
    renderizarCanvas() {
      const dpmm = DPMM
      const w = Math.round(this.medida.anchoMm * dpmm)
      const h = Math.round(this.medida.altoMm * dpmm)
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = '#000'
      ctx.textBaseline = 'alphabetic'

      this.elementos.forEach(el => {
        const x = el.x * dpmm
        const y = el.y * dpmm
        if (el.tipo === 'texto') {
          const px = el.fontMm * dpmm
          ctx.font = `${el.negrita ? 'bold ' : ''}${px}px Arial, sans-serif`
          // Mismo modelo de línea que CSS (line-height 1.2): el glifo se centra
          // en el line box con half-leading, así el render coincide con el editor
          const metricas = ctx.measureText('Mg')
          const lineBox = px * 1.2
          const halfLeading = (lineBox - (metricas.fontBoundingBoxAscent + metricas.fontBoundingBoxDescent)) / 2
          String(el.texto || '').split('\n').forEach((linea, i) => {
            ctx.fillText(linea, x, y + i * lineBox + halfLeading + metricas.fontBoundingBoxAscent)
          })
        } else if (el.img) {
          const anchoPx = el.anchoMm * dpmm
          ctx.drawImage(el.img, x, y, anchoPx, anchoPx * el.ratio)
        }
      })

      // Umbral a blanco/negro puro (la Zebra no imprime grises)
      const data = ctx.getImageData(0, 0, w, h)
      const px = data.data
      for (let i = 0; i < px.length; i += 4) {
        const alpha = px[i + 3] / 255
        // Componer sobre blanco y calcular luminancia
        const r = px[i] * alpha + 255 * (1 - alpha)
        const g = px[i + 1] * alpha + 255 * (1 - alpha)
        const b = px[i + 2] * alpha + 255 * (1 - alpha)
        const lum = 0.299 * r + 0.587 * g + 0.114 * b
        const v = lum < UMBRAL ? 0 : 255
        px[i] = v
        px[i + 1] = v
        px[i + 2] = v
        px[i + 3] = 255
      }
      ctx.putImageData(data, 0, 0)

      return { canvas, ctx, w, h }
    },
    // Convierte el canvas monocromo a un comando ZPL ^GFA
    canvasAZpl(ctx, w, h) {
      const data = ctx.getImageData(0, 0, w, h).data
      const rowBytes = Math.ceil(w / 8)
      const total = rowBytes * h
      let hex = ''
      for (let y = 0; y < h; y++) {
        for (let bx = 0; bx < rowBytes; bx++) {
          let byte = 0
          for (let bit = 0; bit < 8; bit++) {
            const x = bx * 8 + bit
            if (x < w) {
              const i = (y * w + x) * 4
              if (data[i] < 128) byte |= (0x80 >> bit) // negro = 1
            }
          }
          hex += byte.toString(16).padStart(2, '0')
        }
      }
      const copies = (this.copias > 0) ? Math.floor(this.copias) : 1
      // ^LH0,0 y ^LT0 anulan offsets guardados en la impresora de trabajos anteriores
      return `^XA^LH0,0^LT0^PW${w}^LL${h}^FO0,0^GFA,${total},${total},${rowBytes},${hex}^PQ${copies}^XZ`
    },
    verPreview() {
      const { canvas } = this.renderizarCanvas()
      this.previewUrl = canvas.toDataURL('image/png')
      this.showPreview = true
    },
    imprimir() {
      if (!this.printerId) {
        this.notificar('Selecciona una impresora', 'danger')
        return
      }
      const { ctx, w, h } = this.renderizarCanvas()
      const zpl = this.canvasAZpl(ctx, w, h)
      this.imprimiendo = true
      axios.post('/etiquetas/print', {
        zpl,
        printerId: this.printerId,
        title: `Diseñador - ${this.medida.nombre}`,
      })
        .then(() => { this.notificar(`Enviado a imprimir (${this.copias} ud.)`) })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'Error al imprimir'
          this.notificar(msg, 'danger')
        })
        .finally(() => { this.imprimiendo = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.dis-lienzo-wrap {
  overflow-x: auto;
}

.dis-lienzo {
  position: relative;
  margin: 0 auto;
  background: #fff;
  border: 1px dashed #b0b0b8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.dis-el {
  position: absolute;
  cursor: move;
  user-select: none;
  line-height: 1.2;
  border: 1px dashed transparent;

  &:hover {
    border-color: #c9c9d4;
  }

  &.dis-el--sel {
    border-color: #7367f0;
  }

  img {
    display: block;
    pointer-events: none;
  }
}

.dis-el-texto {
  color: #000;
  font-family: Arial, sans-serif;
  white-space: pre;
  display: block;
}

.dis-preview-img {
  max-width: 100%;
  border: 1px solid #ddd;
  image-rendering: pixelated;
}

.dis-input-mm {
  width: 90px;
}

// Bloque de alineación: cuadrícula 3x3 centrada y, debajo, los dos ejes
// ocupando el mismo ancho que la cuadrícula
.dis-alinear-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dis-grid-alinear {
  display: grid;
  grid-template-columns: repeat(3, 36px);
  gap: 4px;

  .btn {
    padding: 5px 0;
  }

  i {
    display: inline-block;
  }
}

.dis-alinear-ejes {
  display: grid;
  grid-template-columns: repeat(2, 56px);
  gap: 4px;
  margin-top: 4px;

  .btn {
    padding: 5px 0;
  }
}

// Lista de elementos añadidos (click = seleccionar en la preview)
.dis-lista-el {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  overflow: hidden;
}

.dis-lista-item {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  cursor: pointer;
  border-bottom: 1px solid #ebe9f1;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f8fb;
  }

  &.dis-lista-item--sel {
    background: rgba(115, 103, 240, 0.08);
  }
}

.dis-lista-icono {
  color: #6e6b7b;
  width: 22px;
  text-align: center;
  margin-right: 8px;
}

.dis-lista-thumb {
  width: 22px;
  height: 22px;
  object-fit: contain;
  margin-right: 8px;
}

.dis-lista-nombre {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9rem;
}

.dis-lista-borrar {
  padding: 4px 8px;
}
</style>
